import { type FC, useEffect, useState, useCallback, useRef } from 'react';
import { createRefetchContainer, graphql, type RelayRefetchProp } from 'react-relay/legacy.js';
import { addLoginHandlers } from 'dibs-buyer-layout/exports/loginHandlers.jsx';
import { getBuyerId } from 'dibs-cookie-jar';
import { OverlaySpinner } from 'dibs-elements/exports/OverlaySpinner.tsx';
import RelatedPhotosRendererSuspense from 'dibs-interiors-photos/exports/RelatedPhotosRendererSuspense.tsx';
import { PhotoRootQuery } from './PhotoRootQuery.ts';
import PhotoContent from './content/PhotoContent.tsx';
import MobilePhotoContent from './content/MobilePhotoContent.tsx';
import { trackPageView } from '../shared/helpers/tracking.ts';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import { type ArrayElement } from 'dibs-ts-utils/exports/ArrayElement.ts';
import { useClientState } from 'dibs-react-hooks/exports/useClientState.ts';
import { useBuildPhotoTitle } from './hooks/useBuildPhotoTitle.ts';
import { usePhotoLookupHistory } from './hooks/usePhotoLookupHistory.ts';
import { useFetchDesignProjectPhotos } from './hooks/useFetchDesignProjectPhotos.ts';
import { useAutosavePhoto } from './hooks/useAutosavePhoto.ts';
import { useServerVars } from '../shared/ServerVarsContext.tsx';
import { useTrackTradePhotoPageview } from 'dibs-interiors-photos/exports/interiorsPhotosTracking.ts';
import { handleLocaleUrl, GLOBAL_CLIENT_ONLY_LOCALE } from 'dibs-intl/exports/urls';
import { InteriorBillboardAd } from 'dibs-interiors-photos/exports/DisplayAds.tsx';
import { PHOTO_TOP_PLACEMENT } from 'dibs-google-ad-manager/exports/constants.ts';

import styles from './InteriorPhotoWrapper.scss';

import { type Photo } from 'dibs-interiors-photos/exports/types.ts';
import { type InteriorPhotoWrapper_viewer$data } from './__generated__/InteriorPhotoWrapper_viewer.graphql.ts';

const getUserId = (): string => getBuyerId(document.cookie);

export type TradePhotoNode = NonNullable<
    ArrayElement<NonNullable<InteriorPhotoWrapper_viewer$data['tradePhotosQuery']>['edges']>
>['node'];

type Props = {
    viewer: InteriorPhotoWrapper_viewer$data;
    relay: RelayRefetchProp;
    isModal?: boolean;
    initialLookupUrl: string;
    prefetchedPhotos?: Photo[];
    onCloseModal?: () => void;
};

const InteriorPhotoWrapper: FC<Props> = props => {
    const { isModal = false, viewer, initialLookupUrl, relay, onCloseModal } = props;
    const [lookupUrl, setLookupUrl] = useState(initialLookupUrl);
    const [nextLookupUrl, setNextLookupUrl] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [userId, setUserId] = useState('');

    const prefetchedPhotos = (props.prefetchedPhotos || []).filter(filterNulls);
    const hasPrefetchedPhotos = prefetchedPhotos.length > 0;
    const [fetchedPhotos, setFetchedPhotos] = useState<Photo[]>(prefetchedPhotos);
    const photoEdges = viewer?.tradePhotosQuery?.edges;

    const findPhotoNodeByUrl = useCallback(
        (url: string): TradePhotoNode | undefined =>
            photoEdges?.find(edge => edge?.node?.photo?.url === url)?.node,
        [photoEdges]
    );

    const photoNode = findPhotoNodeByUrl(lookupUrl) || null;
    const tradePhoto = photoNode?.photo || null;
    const firstPhotoNode = photoEdges?.[0]?.node;
    const firstPhotoUrl = firstPhotoNode?.photo?.url || '';
    const projectRootId = firstPhotoNode?.projectRootId || '';

    const photoId = tradePhoto?.serviceId || '';
    const photoLikeId = tradePhoto?.photoLike?.photoLikeId || '';
    const photoNodeId = tradePhoto?.id || '';

    const userIdRef = useRef('');

    useEffect(() => {
        userIdRef.current = userId;
    }, [userId]);

    useEffect(() => {
        const buyerId = getUserId();
        setUserId(buyerId);

        addLoginHandlers(() => {
            const _userId = getUserId();
            setUserId(_userId);
            relay.refetch(fragmentVars => ({
                ...fragmentVars,
                userId: _userId,
                hasUserId: !!_userId,
            }));
        });
    }, [relay]);

    useEffect(() => {
        trackPageView();
    }, []);

    const handlePageChange = useCallback(
        (nextUrl: string): void => {
            const photoLoaded = !!findPhotoNodeByUrl(nextUrl);
            if (photoLoaded) {
                setLookupUrl(nextUrl);
            } else {
                setIsLoading(true);
                setNextLookupUrl(nextUrl);
            }
        },
        [findPhotoNodeByUrl]
    );

    const photoTitle = useBuildPhotoTitle({
        firmName: photoNode?.firmName,
        photoName: photoNode?.photo?.name,
    });

    const updateHistory = useCallback(
        (path: string, shouldReplace?: boolean) => {
            const fullPath = handleLocaleUrl(
                path + window.location.search,
                GLOBAL_CLIENT_ONLY_LOCALE
            );
            if (shouldReplace) {
                window.history.replaceState(fullPath, photoTitle, fullPath);
            } else {
                window.history.pushState(fullPath, photoTitle, fullPath);
            }
        },
        [photoTitle]
    );

    usePhotoLookupHistory({
        updateHistory,
        isModal,
        photoUrl: initialLookupUrl || firstPhotoUrl || '',
        findPhotoNodeByUrl,
        handlePageChange,
    });

    const isLoadingRef = useRef(false);
    const nextLookupUrlRef = useRef('');
    useEffect(() => {
        isLoadingRef.current = isLoading;
        nextLookupUrlRef.current = nextLookupUrl;
    });

    const [hasFetchedPhotoLikes, setHasFetchedPhotoLikes] = useState(false);

    useTrackTradePhotoPageview(tradePhoto);

    useAutosavePhoto({
        photoId,
        photoLikeId,
        photoNodeId,
        hasFetchedPhotoLikes,
    });

    const onFetchComplete = useCallback(
        (projectPhotos: Photo[]): void => {
            const urlLabels = projectPhotos.map(({ url }) => url);
            relay.refetch(
                { urlLabels, userId: userIdRef.current, hasUserId: !!userIdRef.current },
                null,
                error => {
                    if (error) {
                        // eslint-disable-next-line no-console
                        console.error(error);
                    }
                    setFetchedPhotos(projectPhotos);
                    setHasFetchedPhotoLikes(true);

                    if (isLoadingRef.current && nextLookupUrlRef.current) {
                        setIsLoading(false);
                        setLookupUrl(nextLookupUrlRef.current);
                        setNextLookupUrl('');
                    }
                }
            );
        },
        [relay]
    );

    useFetchDesignProjectPhotos({
        projectRootId,
        relay,
        hasPrefetchedPhotos,
        onFetchComplete,
    });

    const onPageChange = useCallback(
        (nextPath: string) => {
            handlePageChange(nextPath);
            updateHistory(nextPath);
        },
        [handlePageChange, updateHistory]
    );

    const contentProps = {
        onPageChange,
        photoNode,
        photos: fetchedPhotos,
        isModal,
        onCloseModal,
    };

    const { isDesktop, isMobile, areDisplayAdsEnabled } = useServerVars();
    const isClient = useClientState();

    const styleTag = tradePhoto?.styles?.[0];
    const roomTag = tradePhoto?.room;

    return (
        <div className={styles.wrapper}>
            {isDesktop ? (
                <PhotoContent {...contentProps} />
            ) : (
                <MobilePhotoContent {...contentProps} />
            )}
            {areDisplayAdsEnabled && (
                <InteriorBillboardAd placement={PHOTO_TOP_PLACEMENT} isDesktop={isDesktop} />
            )}
            {isClient && (
                <RelatedPhotosRendererSuspense
                    photoId={photoId}
                    isMobile={isMobile}
                    styleTag={styleTag}
                    roomTag={roomTag}
                    userId={userId}
                    isDesktop={isDesktop}
                    areDisplayAdsEnabled={areDisplayAdsEnabled}
                />
            )}
            <OverlaySpinner isOpen={isLoading} />
        </div>
    );
};

export default createRefetchContainer(
    InteriorPhotoWrapper,
    {
        viewer: graphql`
            fragment InteriorPhotoWrapper_viewer on Viewer {
                tradePhotosQuery(urlLabels: $urlLabels) {
                    edges {
                        node {
                            ...PhotoContent_photoNode
                            ...MobilePhotoContent_photoNode
                            projectId
                            projectRootId
                            firmName
                            photo {
                                ...interiorsPhotosTracking_tradePhoto
                                id
                                name
                                url
                                serviceId
                                styles {
                                    name
                                    value
                                    urlLabel
                                }
                                room {
                                    value
                                    name
                                    urlLabel
                                }
                                photoLike {
                                    photoLikeId(userId: $userId) @include(if: $hasUserId)
                                }
                            }
                        }
                    }
                }
            }
        `,
    },
    PhotoRootQuery
);
